// SEM-6040 — Ayarlar › Şirketler sekmesinin arka uç bağdaştırıcıları.
// Sözleşme: services/platform-graph/src/routes/legal-entities.ts.
//
// Bu sekme "Kurum Bilgileri" sekmesinden AYRI bir şeydir ve ikisi
// karıştırılmamalıdır: Kurum Bilgileri `entity_tenant_setting` üzerindeki
// HOLDİNG tercihlerini (görünen ad, saat dilimi, mali yıl) tutar; burası
// holdingin İŞLETTİĞİ tüzel kişilikleri — çalışanın bağlı olduğu, bordronun
// VKN'sini bastığı satırları — yönetir. Bir holdingin bir tercih kümesi, ama
// birden çok şirketi vardır.
//
// Yazma uçları yeni olduğu için `requestBody` üretilen tiplerde henüz `never`
// görünüyor; users-api.ts ile aynı dar `platformGraph.<VERB> as unknown as
// <Typed>` köprüsü kullanılıyor.

import { platformGraph } from "@/api/platform-graph";
import { unwrapEnvelope, type EnvelopedFetchResult } from "@/api/errors";

export interface CompanyRow {
  readonly legal_entity_id: string;
  readonly name: string;
  readonly country: string;
  readonly currency: string;
  readonly tax_ids: Record<string, unknown>;
  readonly registration_number: string | null;
  readonly mersis_no: string | null;
  readonly address: Record<string, unknown> | null;
  readonly is_default: boolean;
  readonly is_eor: boolean;
  readonly is_peo: boolean;
  readonly lifecycle_state: string;
  /**
   * null = satırı sistem yarattı (kiracı sağlaması veya 0213 backfill'i).
   * Ekran bunu "doğrulanmadı" rozetiyle gösterir; hiçbir insan bu şirketin
   * bilgilerini onaylamamıştır.
   */
  readonly created_by: string | null;
  readonly updated_by: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

export interface CompanyDraft {
  readonly name: string;
  readonly country: string;
  readonly currency: string;
  readonly tax_ids?: Record<string, string>;
  readonly registration_number?: string;
  readonly mersis_no?: string;
  readonly address?: Record<string, string>;
}

export type CompanyPatch = Partial<CompanyDraft>;

/** Her çağrıda taze anahtar (spec `idempotency: required`). */
function idempotencyHeader(): Record<string, string> {
  return { "x-idempotency-key": crypto.randomUUID() };
}

/** Şirketler sekmesi ve Kurum Bilgileri kahramanı aynı listeyi paylaşır. */
export const companiesQueryKey = ["legal-entities"] as const;

type ListGet = (
  path: "/v1/legal-entities",
  init: { params: { query: { lifecycle_state?: "active" | "archived" } } },
) => Promise<EnvelopedFetchResult<{ items: CompanyRow[] }>>;

export async function fetchCompanies(
  lifecycleState?: "active" | "archived",
): Promise<readonly CompanyRow[]> {
  const get = platformGraph.GET as unknown as ListGet;
  const result = await unwrapEnvelope(
    get("/v1/legal-entities", {
      params: { query: { ...(lifecycleState !== undefined && { lifecycle_state: lifecycleState }) } },
    }),
  );
  return result.items;
}

type CreatePost = (
  path: "/v1/legal-entities",
  init: { body: CompanyDraft; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ legal_entity: CompanyRow }>>;

export async function createCompany(draft: CompanyDraft): Promise<CompanyRow> {
  const post = platformGraph.POST as unknown as CreatePost;
  const result = await unwrapEnvelope(
    post("/v1/legal-entities", { body: draft, headers: idempotencyHeader() }),
  );
  return result.legal_entity;
}

type UpdatePatch = (
  path: "/v1/legal-entities/{legal_entity_id}",
  init: { params: { path: { legal_entity_id: string } }; body: CompanyPatch },
) => Promise<EnvelopedFetchResult<{ legal_entity: CompanyRow }>>;

export async function updateCompany(
  legalEntityId: string,
  patch: CompanyPatch,
): Promise<CompanyRow> {
  const request = platformGraph.PATCH as unknown as UpdatePatch;
  const result = await unwrapEnvelope(
    request("/v1/legal-entities/{legal_entity_id}", {
      params: { path: { legal_entity_id: legalEntityId } },
      body: patch,
    }),
  );
  return result.legal_entity;
}

type ArchivePost = (
  path: "/v1/legal-entities/{legal_entity_id}/archive",
  init: { params: { path: { legal_entity_id: string } }; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ legal_entity: CompanyRow }>>;

export async function archiveCompany(legalEntityId: string): Promise<CompanyRow> {
  const post = platformGraph.POST as unknown as ArchivePost;
  const result = await unwrapEnvelope(
    post("/v1/legal-entities/{legal_entity_id}/archive", {
      params: { path: { legal_entity_id: legalEntityId } },
      headers: idempotencyHeader(),
    }),
  );
  return result.legal_entity;
}
